import { useCallback, useState } from 'react'
import type { JSX } from 'react'
import { Link, useLocation } from 'react-router-dom'

import type { ErrorLike } from '@apollo/client'
import { clsx } from 'clsx'

import { useSplitEnabled } from '@buffer-mono/features'
import {
  IconButton,
  MessageSquareQuoteIcon,
  SendHorizontalIcon,
  Skeleton,
  toast,
  Tooltip,
  ZapIcon,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { useDateFormatter } from '~publish/hooks/useDateTimeFormatter'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import { isRpcUpdate } from '~publish/legacy/post/helpers'
import { communityPage } from '~publish/legacy/routes'
import { useFetchPostForComposer } from '~publish/hooks/postForComposer/useFetchPostForComposer'
import { canPerform } from '~publish/utils/permissions/canPerform'

import type { TopPostsSortBy } from '../../graphqlEnums'
import { ChartErrorState } from '../../components/ChartErrorState'
import { TopPostCard as TopPostCardView } from '../../components/TopPostCard'
import { NoDataEmptyState } from '../../components/NoDataEmptyState'
import type { TopPost } from '../../hooks'
import {
  formatInsightsDateRange,
  formatNumber,
  getLinkedInBoostUrl,
  getNoPostsDescription,
} from '../../utils'

import { TopPostsEmptyCard, TopPostsSectionView } from './TopPostsSectionView'
import styles from './AllChannelsTopPosts.module.css'

function getMetricValue(post: TopPost, sortBy: TopPostsSortBy): number {
  const metric = post.metrics?.find((m) => m.type === sortBy)
  return metric?.value ?? 0
}

function TopPostCardActions({
  postId,
  channelId,
  allowedActions,
  canCreatePosts,
  boostUrl,
}: {
  postId: string
  channelId: string
  allowedActions: readonly string[]
  /** When false, hides the Duplicate action (duplicating opens the composer). */
  canCreatePosts: boolean
  boostUrl?: string | null
}): JSX.Element {
  const organizationId = useOrganizationId()
  const { triggerAttributes, duplicatePostInComposer } = usePostComposer()
  const fetchPostForComposer = useFetchPostForComposer()
  const [duplicateLoading, setDuplicateLoading] = useState(false)

  const handleBoostClick = useCallback(() => {
    BufferTracker.cTAClicked({
      clientName: 'publishWeb',
      cta: 'publish-insights-allChannelsTopPosts-boostLinkedIn-1',
      upgradePathName: null,
      organizationId: organizationId ?? undefined,
      product: 'publish',
    })
  }, [organizationId])

  const canDuplicate = allowedActions.includes('duplicatePost')

  const handleDuplicate = useCallback(async () => {
    setDuplicateLoading(true)
    try {
      const post = await fetchPostForComposer(postId)
      if (!isRpcUpdate(post)) {
        toast.error('Failed to load post for duplication')
        return
      }

      const {
        id: _id,
        due_at: _dueAt,
        day: _day,
        scheduled_at: _scheduledAtSnake,
        scheduledAt: _scheduledAt,
        serviceUpdateId: _serviceUpdateId,
        createdAt: _createdAt,
        statistics: _statistics,
        status: _status,
        serviceLink: _serviceLink,
        tags,
        ...rest
      } = post

      void duplicatePostInComposer({
        cta: 'publish-insights-allChannelsTopPosts-duplicate-1',
        channels: [channelId],
        prefillPostData: {
          ...rest,
          tags: tags?.map((tag: { id: string }) => tag.id),
          isSent: false,
          isPastDue: false,
          isDraft: false,
        },
        duplicatedFrom: postId,
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err)
      toast.error(`Failed to duplicate post: ${message}`)
    } finally {
      setDuplicateLoading(false)
    }
  }, [postId, channelId, duplicatePostInComposer, fetchPostForComposer])

  return (
    <div className={styles.cardFooter}>
      <IconButton
        as={Link}
        to={communityPage.getViewRoute({
          view: 'posts',
          postId,
        })}
        label="View comments"
        tooltip="View comments"
        size="small"
      >
        <MessageSquareQuoteIcon size="xsmall" />
      </IconButton>
      {boostUrl && (
        <IconButton
          as="a"
          href={boostUrl}
          target="_blank"
          rel="noopener noreferrer"
          label="Boost on LinkedIn"
          tooltip="Boost on LinkedIn"
          size="small"
          onClick={handleBoostClick}
        >
          <ZapIcon size="xsmall" />
        </IconButton>
      )}
      {/* Hidden, not disabled, without posting access — the disabled explainer
          below is about non-Buffer posts, not permissions. */}
      {canCreatePosts &&
        (canDuplicate ? (
          <IconButton
            {...triggerAttributes}
            label="Duplicate post"
            tooltip="Duplicate post"
            size="small"
            loading={duplicateLoading}
            onClick={handleDuplicate}
          >
            <SendHorizontalIcon size="xsmall" />
          </IconButton>
        ) : (
          <Tooltip content="We can only duplicate posts shared via Buffer">
            {/* Wrapper is the tooltip trigger: a disabled <button> doesn't fire
                hover events, so the tooltip can't anchor to it directly. */}
            <span className={styles.disabledActionWrapper}>
              <IconButton label="Duplicate post" size="small" disabled>
                <SendHorizontalIcon size="xsmall" />
              </IconButton>
            </span>
          </Tooltip>
        ))}
    </div>
  )
}

function TopPostCard({
  post,
  rank,
  sortBy,
}: {
  post: TopPost
  rank: number
  sortBy: TopPostsSortBy
}): JSX.Element {
  const { search } = useLocation()
  const formatDate = useDateFormatter()
  const { isEnabled: isBoostEnabled } = useSplitEnabled(
    'geid-linkedin-boost-ctas',
  )
  const metricValue = getMetricValue(post, sortBy)
  const metricNoun = sortBy === 'reactions' ? 'Reaction' : 'Comment'
  const metricLabel = metricValue === 1 ? metricNoun : `${metricNoun}s`
  const formattedDate = post.sentAt
    ? formatDate(post.sentAt, {
        dateFormat: 'MMM d, yyyy',
        includeTimeZone: false,
      })
    : null

  return (
    <TopPostCardView
      label={`#${rank} post with ${formatNumber(metricValue)} ${metricLabel.toLowerCase()}`}
    >
      <TopPostCardView.Header
        rank={rank}
        metricValue={metricValue}
        metricType={sortBy === 'reactions' ? 'reactions' : 'comments'}
      />
      <TopPostCardView.Preview>
        <TopPostCardView.Metadata
          service={post.channelService}
          date={formattedDate}
          postType={post.metadata?.type}
        />
        <Link
          to={`posts/${post.id}${search}`}
          className={styles.postLink}
          {...(!post.text && { 'aria-label': 'View post details' })}
        >
          <TopPostCardView.Content text={post.text} assets={post.assets} />
        </Link>

        {post.tags.length > 0 && (
          <ul className={styles.tagList} aria-label="Tags">
            {post.tags.map((tag) => (
              <Tooltip
                key={tag.id}
                content={<span translate="no">{tag.name}</span>}
              >
                <li
                  className={styles.tagPill}
                  style={{ backgroundColor: tag.color }}
                  aria-label={tag.name}
                />
              </Tooltip>
            ))}
          </ul>
        )}

        <TopPostCardActions
          postId={post.id}
          channelId={post.channelId}
          allowedActions={post.allowedActions}
          canCreatePosts={canPerform(post.channel, 'manageUpdates')}
          boostUrl={
            isBoostEnabled && post.channelService === 'linkedin'
              ? getLinkedInBoostUrl(post.externalLink, post.channel?.serviceId)
              : null
          }
        />
      </TopPostCardView.Preview>
    </TopPostCardView>
  )
}

interface AllChannelsTopPostsProps {
  topPosts: TopPost[]
  loading: boolean
  refreshing?: boolean
  error: ErrorLike | undefined
  startDate: Date
  endDate: Date
  /** When set, renders the section subtitle as "From … · Compared to …". */
  comparisonStartDate?: Date | null
  comparisonEndDate?: Date | null
  sortBy: TopPostsSortBy
  onSortByChange: (sortBy: TopPostsSortBy) => void
  selectedDateRange?: string
  hasChannelFilter?: boolean
  onClearChannelFilter?: () => void
}

export function AllChannelsTopPosts({
  topPosts,
  loading,
  refreshing = false,
  error,
  startDate,
  endDate,
  comparisonStartDate,
  comparisonEndDate,
  sortBy,
  onSortByChange,
  selectedDateRange,
  hasChannelFilter,
  onClearChannelFilter,
}: AllChannelsTopPostsProps): JSX.Element {
  const dateRange = formatInsightsDateRange(
    startDate,
    endDate,
    comparisonStartDate,
    comparisonEndDate,
  )

  if (loading) {
    return (
      <TopPostsSectionView
        subtitle={dateRange}
        sortBy={sortBy}
        onSortByChange={onSortByChange}
      >
        <Skeleton height={240} />
      </TopPostsSectionView>
    )
  }

  if (error) {
    return (
      <TopPostsSectionView
        subtitle={dateRange}
        sortBy={sortBy}
        onSortByChange={onSortByChange}
      >
        <ChartErrorState />
      </TopPostsSectionView>
    )
  }

  return (
    <TopPostsSectionView
      subtitle={dateRange}
      sortBy={sortBy}
      onSortByChange={onSortByChange}
    >
      {topPosts.length > 0 ? (
        <div className={styles.cardsGridContainer}>
          <div
            className={clsx(styles.cardsGrid, refreshing && styles.refreshing)}
            // Articles retain their own semantics while this named collection remains a list.
            // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
            role="list"
            aria-busy={refreshing}
          >
            {topPosts.map((post, index) => (
              <TopPostCard
                key={post.id}
                post={post}
                rank={index + 1}
                sortBy={sortBy}
              />
            ))}
          </div>
        </div>
      ) : (
        <TopPostsEmptyCard>
          <NoDataEmptyState
            description={getNoPostsDescription(
              selectedDateRange,
              hasChannelFilter,
            )}
            showSuggestion
            onClearChannelFilter={onClearChannelFilter}
          />
        </TopPostsEmptyCard>
      )}
    </TopPostsSectionView>
  )
}
